/**
 * A filter control for a facet with more values than a native `<select>` can serve.
 *
 * The corpus has 48 regions and 42 years. A native select renders them as an OS menu with
 * no search, so choosing "Pidie Jaya" means scrolling a list sorted by document count —
 * and the menu is drawn by the platform, so it ignores the app's own styling entirely.
 *
 * Radix Popover instead: a trigger that reads as a chip when a value is chosen, a filter
 * box, and a scrollable list. Keyboard and focus behaviour come from Radix rather than
 * being reimplemented.
 *
 * THE COUNTS ARE PART OF THE CONTRACT. Each option shows how many documents choosing it
 * yields, computed under the OTHER filters already applied (`GET /corpus/facets`). Showing
 * a corpus-wide count next to a value that returns far fewer is the defect this component
 * was built alongside: "2023 (234)" that returned 29.
 */
import * as Popover from "@radix-ui/react-popover";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/primitives.tsx";
import type { FacetValue } from "@/lib/api-types.ts";

/** Below this a filter box is clutter; above it, scrolling alone is the problem. */
const SEARCHABLE_FROM = 12;

export function FacetSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
  loading = false,
}: {
  /** What this filters, e.g. "Wilayah". Shown when nothing is chosen, and as the a11y name. */
  label: string;
  /** The clear-selection entry, e.g. "Semua wilayah". */
  allLabel: string;
  value: string;
  options: FacetValue[];
  onChange: (value: string) => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState("");

  const searchable = options.length >= SEARCHABLE_FROM;
  const shown = useMemo(() => {
    const query = needle.trim().toLowerCase();
    return query ? options.filter((option) => option.value.toLowerCase().includes(query)) : options;
  }, [options, needle]);

  const total = useMemo(
    () => options.reduce((sum, option) => sum + option.count, 0),
    [options],
  );

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setNeedle("");
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset the filter box on close, so reopening never shows a list narrowed by a
        // query the user cannot see.
        if (!next) setNeedle("");
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className={`facet-trigger${value ? " is-set" : ""}`}
          aria-label={label}
          disabled={loading && options.length === 0}
        >
          <span className="facet-trigger-label">{value || label}</span>
          <span className="facet-trigger-caret" aria-hidden="true">
            ▾
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="facet-pop" sideOffset={6} align="start">
          {searchable ? (
            <div className="facet-search">
              <Input
                autoFocus
                value={needle}
                onChange={(event) => setNeedle(event.target.value)}
                placeholder={t("library.facetSearch")}
                aria-label={t("library.facetSearch")}
              />
            </div>
          ) : null}
          <div className="facet-list" role="listbox" aria-label={label}>
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              className={`facet-option${value === "" ? " is-active" : ""}`}
              onClick={() => choose("")}
            >
              <span>{allLabel}</span>
              <span className="facet-count">{total}</span>
            </button>
            {shown.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value === option.value}
                className={`facet-option${value === option.value ? " is-active" : ""}`}
                onClick={() => choose(option.value)}
              >
                <span>{option.value}</span>
                <span className="facet-count">{option.count}</span>
              </button>
            ))}
            {shown.length === 0 ? (
              // Says WHY the list is empty. A blank panel reads as a broken control, and
              // the fix — clearing the box — is not guessable from nothing.
              <p className="facet-empty">{t("library.facetNoMatch", { needle: needle.trim() })}</p>
            ) : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
